import { ref } from "vue";
import { useShepherd } from "vue-shepherd";
import { waitForElement } from "../utils/wait-for-element";
import { sendProductTourStep } from "@/follaw-fr/services/apiUsers";

const TOUR_STEP = Object.freeze({
  WELCOME: "WELCOME",
  DASHBOARDS: "DASHBOARDS",
  TOPBAR: "TOPBAR",
  SECTIONS: "SECTIONS",
  FOLLAWED: "FOLLAWED",
  SOCIAL_MEDIA: "SOCIAL_MEDIA",
  ALERTS: "ALERTS",
  ALERTS_ZOOM: "ALERTS_ZOOM",
  BYE_BYE: "BYE_BYE",
});

export const configProductTour = (router) => {
  const alreadySendStep = ref([]);

  const tour = useShepherd({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: "shepherd-theme-custom",
    },
  });

  const createProgressIndicator = (currentStep, totalSteps) => {
    const progress = document.createElement("span");
    progress.className = "shepherd-progress";
    progress.innerText = `étape ${currentStep + 1}/${totalSteps}`;
    return progress;
  };

  const addProgressIndicator = (footer, currentStep, totalSteps) => {
    const existingProgress = footer.querySelector(".shepherd-progress");
    const progress = createProgressIndicator(currentStep, totalSteps);

    if (existingProgress) {
      existingProgress.innerText = progress.innerText;
    } else {
      footer.insertBefore(
        progress,
        footer.querySelector(".shepherd-button:first-child")
      );
    }
  };

  const updateProgressIndicator = () => {
    const currentStep = tour.getCurrentStep();
    const currentStepElement = currentStep?.getElement();
    if (!currentStepElement) return;

    const footer = currentStepElement.querySelector(".shepherd-footer");
    if (!footer) return;

    const stepIndex = tour.steps.indexOf(currentStep);
    addProgressIndicator(footer, stepIndex, tour.steps.length);
  };

  const trackTourStep = async (step) => {
    if (!step) return;
    if (alreadySendStep.value.includes(step)) return;

    alreadySendStep.value.push(step);
    await sendProductTourStep(step);
  };
  tour.addStep({
    id: TOUR_STEP.WELCOME,
    arrow: false,
    attachTo: {
      element: "[data-product-tour='step-1']",
      on: "bottom",
    },
    title: `Bienvenue sur Follaw.sv, <br>
      outil de veille sociétale et institutionnelle !`,
    text: `
      <p class="product-tour__subtitle">Notre mission ?</p>
      <p class="product-tour-content-text--center">Démocratiser les affaires publiques en rendant accessible et facile l’accès aux informations institutionnelles.</p>
      <p class="product-tour-content-text--center">Merci de rejoindre la communauté !</p>
    `,
    buttons: [
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: async () => {
          trackTourStep(TOUR_STEP.WELCOME);
          // Open the Dashboards menu
          document.querySelector("[data-product-tour='step-2-1']").click();
          await waitForElement("[data-product-tour='step-2-2']");
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.DASHBOARDS,
    attachTo: {
      element: "[data-product-tour='step-2-2']",
      on: "right",
    },
    extraHighlights: ["[data-product-tour='step-2-1']"],
    modalOverlayOpeningRadius: 13,
    text: `
      <p class="product-tour__subtitle">Vous avez ajouté votre paramétrage ici</p>
      <span class="product-tour-content-text">
        Ce paramétrage définit toute votre expérience, le contenu et les filtres auxquels vous allez avoir accès.
        Vous pouvez le reparamètrer facilement en cliquant ici.
      </span>
    `,
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: tour.back,
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: () => {
          // Close the Dashboards menu
          document.querySelector("[data-product-tour='step-2-1']").click();
          // Open topbar responsive overflow on small screens, does nothing on bigger screens
          document.querySelector("[data-product-tour='step-3-2']").click();
          trackTourStep(TOUR_STEP.DASHBOARDS);
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.TOPBAR,
    attachTo: {
      element: "[data-product-tour='step-3-3']",
      on: "bottom",
    },
    extraHighlights: [
      "[data-product-tour='step-3-1']",
      "[data-product-tour='step-3-2']",
      "[data-product-tour='step-3-4']",
    ],
    modalOverlayOpeningRadius: 15,
    text: `
      <p class="product-tour__subtitle">Accédez facilement à toutes les informations pertinentes en quelques clics !</p>
      <span class="product-tour-content-text">La barre de filtres permet d’affiner vos recherches selon vos critères :</span>
      <ul class="product-tour-content-list">
        <li>Choisissez la temporalité qui vous convient</li>
        <li>Sélectionnez parmi 850 thématiques et sous-thématiques paramétrées par notre équipe et prêtes à l’emploi</li>
        <li>Affinez votre recherche selon vos propres mots-clés</li>
        <li>Ces filtres peuvent être enregistrés via le bouton “veille personnalisée” (voir étape précédente)</li>
      </ul>
    `,
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: async () => {
          // Open the Dashboards menu
          document.querySelector("[data-product-tour='step-2-1']").click();
          await waitForElement("[data-product-tour='step-2-2']");
          tour.back();
        },
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: () => {
          trackTourStep(TOUR_STEP.TOPBAR);
          // Open the side menu category
          document
            .querySelector("[aria-label='Travaux parlementaires']")
            .click();
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.SECTIONS,
    attachTo: {
      element: "[data-product-tour='step-4']",
      on: "right",
    },
    text: "Les différentes informations de veilles sont accessibles via ces sections. Pour commencer, nous allons explorer les dossiers législatifs récents.",
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: () => {
          // Closes the side menu category
          document
            .querySelector("[aria-label='Travaux parlementaires']")
            .click();
          // Open topbar responsive overflow on small screens, does nothing on bigger screens
          document.querySelector("[data-product-tour='step-3-2']").click();
          tour.back();
        },
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: async () => {
          trackTourStep(TOUR_STEP.SECTIONS);
          await router.push({
            name: "Dossiers législatifs",
            query: {
              search: undefined,
              thematics: undefined,
            },
          });
          tour.hide();
          await waitForElement("[data-product-tour='step-5']");
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.FOLLAWED,
    attachTo: { element: "[data-product-tour='step-5']", on: "bottom" },
    modalOverlayOpeningRadius: 15,
    modalOverlayOpeningPadding: 2,
    // TODO: Probably can be removed if the Follaw button is refactore properly
    modalOverlayOpeningYOffset: 1,
    modalOverlayOpeningXOffset: 1,
    // TODO: End of Todo
    text: `
      <p class="product-tour__subtitle">Vous souhaitez marquer un élément en favori pour le suivre facilement ?</p>
      <span class="product-tour-content-text">Avec Follaw, vous pouvez :</span>
      <ul class="product-tour-content-list">
        <li>Conserver (et retrouver) facilement en favori les éléments souhaités</li>
        <li>Recevoir des alertes en cas de mise à jour sur ces éléments (Soyez immédiatement informé des nouvelles informations : réponses à une question, des mises à jour d'un dossier législatif, du sort d'un amendement, etc.)</li>
      </ul>
      <p>Vous pouvez ensuite naviguer efficacement entre les contenus qui vous intéressent et ceux que vous suivez de près avec les boutons "Follawed" et "Tous les matchs".</p>
    `,
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: () => {
          router.push("/");
          tour.back();
        },
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: async () => {
          trackTourStep(TOUR_STEP.FOLLAWED);
          // Open the side menu category
          document.querySelector("[aria-label='Société']").click();
          await router.push({
            name: "Overview",
            query: {
              search: undefined,
              thematics: undefined,
            },
          });
          tour.hide();
          await waitForElement("[data-product-tour='step-6']");
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.SOCIAL_MEDIA,
    attachTo: { element: "[data-product-tour='step-6']", on: "left" },
    modalOverlayOpeningRadius: 4,
    text: `Vous souhaitez aller encore plus loin, avec une veille globale regroupant une surveillance des institutions mais aussi des informations sociétales pouvant impacter vos enjeux ?
    </br>
    Follaw.SV vous permet dès maintenant d’être alerté et d’analyser efficacement toutes les prises de parole des parties prenantes influentes : médias, journalistes, lobbies, politiques... Et ce, en fonction de vos enjeux ! 
    </br>
    Nous avons sélectionné pour vous, un panel de plus de 20 000 personnalités clés au niveau français et européen pour que vous puissiez anticiper les changements sociétaux... qui impacteront par la suite les prochaines évolutions politiques.
    `,
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: async () => {
          // Closes the side menu category
          document
            .querySelector("[aria-label='Travaux parlementaires']")
            .click();
          await router.push({
            name: "Dossiers législatifs",
            query: {
              search: undefined,
              thematics: undefined,
            },
          });
          tour.hide();
          await waitForElement("[data-product-tour='step-5']");
          tour.back();
        },
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: () => {
          trackTourStep(TOUR_STEP.SOCIAL_MEDIA);
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.ALERTS,
    attachTo: { element: "[data-product-tour='step-7']", on: "bottom" },
    modalOverlayOpeningRadius: 15,
    modalOverlayOpeningPadding: 2,
    text: `
      <p class="product-tour__subtitle">Vous préférez que l'information vienne à vous ?</p>
      <p>Notre système d’alerte est la solution ! </p>
      </br>
      <span class="product-tour-content-text">Paramétrez vos alertes afin de vous faire gagner un temps précieux soit :</span>
      <ul class="product-tour-content-list">
        <li>via des alertes en temps réel pour vous tenir informé des dernières actualités institutionnelles ou sociétales.</li>
        <li>via des récapitulatifs des informations sur le corpus de veille et la période souhaités</li>
      </ul>
      <p>Laissez l’outil travailler pour vous et restez en permanence informé !</p>
      </br>
      <p>Voyons ça de plus près à l’étape suivante</p>
    `,
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: tour.back,
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: async () => {
          trackTourStep(TOUR_STEP.ALERTS);
          // Close the side menu category
          document.querySelector("[aria-label='Société']").click();
          await router.push({
            name: "Alerting - Récapitulatifs",
            query: {
              search: undefined,
              thematics: undefined,
            },
          });
          tour.hide();
          await waitForElement("[data-product-tour='step-8']");
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.ALERTS_ZOOM,
    attachTo: { element: "[data-product-tour='step-8']", on: "bottom" },
    modalOverlayOpeningRadius: 4,
    text: "Nous vous avons créé un récapitulatif sur base de votre veille personnalisée. N’hésitez pas à la modifier et à l’adapter à vos besoin !",
    buttons: [
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: () => {
          // Open the side menu category
          document.querySelector("[aria-label='Social Media']").click();
          router.push({ name: "Centre de contrôle" });
          tour.back();
        },
      },
      {
        text: "Suivant",
        classes: "shepherd-button-primary",
        action: () => {
          trackTourStep(TOUR_STEP.ALERTS_ZOOM);
          tour.next();
        },
      },
      {
        text: `<i class="fa-solid fa-xmark" />`,
        action: tour.complete,
        classes: "shepherd-button-cancel",
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });
  tour.addStep({
    id: TOUR_STEP.BYE_BYE,
    attachTo: {
      element:
        import.meta.env.VITE_ENV === "production"
          ? "[aria-label='Ouvrir le chat']"
          : document.querySelector("[aria-label='Ouvrir le chat']")
            ? "[aria-label='Ouvrir le chat']"
            : "[data-product-tour='step-8']",
      on: "right",
    },
    modalOverlayOpeningRadius: 30,
    modalOverlayOpeningPadding: 2,
    text: `
      <p class="product-tour__subtitle">Félicitations !</p>
      <p>
        Vous êtes désormais non seulement dans le mouvement de la modernisation des affaires publiques,
        mais vous êtes également au fait de toutes les fonctionnalités clés de la plateforme.
      </p>
        <p>Vous avez une question ? Une demande particulière ?<br>
        Vous pouvez contacter notre service client directement pour obtenir des éclaircissements sur une utilisation optimale,
        l'ajout de nouvelles fonctionnalités, ou toute autre demande similaire.
      </p>
    `,
    buttons: [
      {
        text: "Recommencer",
        classes: "shepherd-button-secondary",
        action: async () => {
          trackTourStep(TOUR_STEP.BYE_BYE);
          tour.cancel();
          await router.push("/");
          tour.start();
        },
      },
      {
        text: "Retour",
        classes: "shepherd-button-secondary",
        action: tour.back,
      },
      {
        text: "Terminer",
        classes: "shepherd-button-primary",
        action: () => {
          trackTourStep(TOUR_STEP.BYE_BYE);
          tour.complete();
        },
      },
    ],
    when: {
      show: () => updateProgressIndicator(),
    },
  });

  return tour;
};
